import React from 'react';
import FormField from '../shared/FormField';
import TextAreaField from '../shared/TextAreaField';
import CheckboxField from '../shared/CheckboxField';

// Temporary placeholder for icons - replace with your preferred icon library
const WrenchScrewdriverIcon = ({ className }) => <span className={className}>🔧</span>;

/**
 * EquipmentPersonnelInvolvement - Form section for equipment and personnel involved in the safety event
 * 
 * This section captures:
 * - Equipment involved in the event
 * - Tools involved in the event
 * - Part numbers involved
 * - Other personnel involved
 * - Contractor involvement details
 * 
 * Fields align with database schema:
 * - equipment_involved (TEXT)
 * - tools_involved (TEXT)
 * - part_numbers_involved (TEXT)
 * - other_personnel_involved (TEXT)
 * - contractor_involved (BOOLEAN)
 * - contractor_company (TEXT, conditional)
 */

const FormSection = ({ title, children, icon }) => (
  <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-6">
    <div className="flex items-center space-x-3 mb-6">
      {icon}
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
    </div>
    {children}
  </div>
);

const EquipmentPersonnelInvolvement = ({ 
  formData, 
  updateFormData, 
  errors = {}, 
  loading = false 
}) => {
  const handleInputChange = (field, value) => {
    updateFormData({ [field]: value });
  };

  return (
    <FormSection 
      title="Equipment & Personnel Involvement" 
      icon={<WrenchScrewdriverIcon className="h-6 w-6 text-blue-600" />}
    >
      <div className="space-y-6">
        {/* Equipment Information Section */}
        <div className="grid grid-cols-1 gap-6">
          <TextAreaField
            label="Equipment Involved"
            name="equipment_involved"
            rows={3}
            value={formData.equipment_involved || ''}
            onChange={(updates) => updateFormData(updates)}
            error={errors.equipment_involved}
            placeholder="List any equipment, machinery, or systems involved in the event (e.g., hydraulic system, ground support equipment, test equipment)"
            disabled={loading}
            maxLength={1000}
            showWordCount={true}
            helperText="Include equipment serial numbers, model numbers, or identifiers where relevant"
          />
          
          <TextAreaField
            label="Tools Involved"
            name="tools_involved"
            rows={3}
            value={formData.tools_involved || ''}
            onChange={(updates) => updateFormData(updates)}
            error={errors.tools_involved}
            placeholder="List any tools used or involved in the event (e.g., torque wrench, multimeter, safety equipment)"
            disabled={loading}
            maxLength={1000}
            showWordCount={true}
            helperText="Include tool calibration numbers or specifications if relevant to the event"
          />
          
          <TextAreaField
            label="Part Numbers Involved"
            name="part_numbers_involved"
            rows={2}
            value={formData.part_numbers_involved || ''}
            onChange={(updates) => updateFormData(updates)}
            error={errors.part_numbers_involved}
            placeholder="List relevant part numbers (e.g., 123-45678-901, Service Bulletin SB123-456)"
            disabled={loading}
            maxLength={500}
            showWordCount={true}
            helperText="Include manufacturer part numbers, service bulletins, or component identifiers"
          />
        </div>

        {/* Personnel Information Section */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Personnel Involvement</h3>
          
          <div className="space-y-6">
            <TextAreaField
              label="Other Personnel Involved"
              name="other_personnel_involved"
              rows={4}
              value={formData.other_personnel_involved || ''}
              onChange={(updates) => updateFormData(updates)}
              error={errors.other_personnel_involved}
              placeholder="List other personnel involved in the event (names, positions, departments, roles in the event)"
              disabled={loading}
              maxLength={1000}
              showWordCount={true}
              helperText="Include witnesses, supervisors, or others who were present or involved"
            />
            
            {/* Contractor Involvement */}
            <div className="space-y-4">
              <CheckboxField
                label="Contractor was involved in this event"
                name="contractor_involved"
                checked={formData.contractor_involved || false}
                onChange={(updates) => updateFormData(updates)}
                error={errors.contractor_involved}
                disabled={loading}
                description="Check if any external contractor or vendor personnel were involved"
              />
              
              {/* Conditional contractor company field */}
              {formData.contractor_involved && (
                <div className="ml-6 pl-4 border-l-2 border-blue-200 bg-blue-50 rounded-r p-4">
                  <FormField
                    label="Contractor Company *"
                    name="contractor_company"
                    type="text"
                    value={formData.contractor_company || ''}
                    onChange={(e) => handleInputChange('contractor_company', e.target.value)}
                    error={errors.contractor_company}
                    placeholder="Name of contractor company"
                    disabled={loading}
                    maxLength={100}
                    required={formData.contractor_involved}
                    helperText="Provide the full company name of the contractor involved"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Additional Context Section */}
        <div className="border-t border-gray-200 pt-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              Tips for Complete Equipment & Personnel Information
            </h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Include specific equipment models, serial numbers, or identifiers</li>
              <li>• Note if any equipment was out of calibration or had known issues</li>
              <li>• List all personnel who witnessed or were involved in the event</li>
              <li>• Include contractor personnel and their specific roles</li>
              <li>• Note if any special tools or procedures were being used</li>
              <li>• Mention if any equipment or tools were damaged in the event</li>
            </ul>
          </div>
        </div>

        {/* Form state indicators */}
        {loading && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-gray-600">Loading...</span>
          </div>
        )}

        {/* Error summary */}
        {Object.keys(errors).length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Please correct the following errors:
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <ul className="list-disc list-inside space-y-1">
                    {Object.entries(errors).map(([field, error]) => (
                      <li key={field}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </FormSection>
  );
};

export default EquipmentPersonnelInvolvement;
